import { createInvocationId, logTemplateWebMCP } from '../logging';
import { mcpToolResult, toolError, toolSuccess, type TemplateWebMCPToolResult } from '../responses';
import { searchReference } from '../referenceIndex';
import type { TemplateToolContext } from '../toolContext';

export const TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL = {
  name: 'template_builder_search_reference',
  description:
    'Search static Template Builder shapes. This does not read the open template and does not write. Kinds are operation, element_type, display_options, source_family, and record_shape. record_shape entryIds are data_point, advertiser, and brand. Input is { query?, entryId?, kind?, limit?, includePayload? }. An exact entryId returns that payload. Live advertiser, data-point, and brand rows stay on template_builder_read. Returns { ok, data?: { entries }, error? }. Error code is UNAUTHORIZED, INVALID_INPUT or UNEXPECTED.',
  enabled: true,
  annotations: {
    title: 'Search Template Builder reference',
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
  },
  inputSchema: {
    type: 'object',
    properties: {
      query: { type: 'string' },
      entryId: { type: 'string' },
      kind: { type: 'string' },
      limit: { type: 'integer' },
      includePayload: { type: 'boolean' },
    },
    additionalProperties: false,
  } as const,
  outputSchema: {
    type: 'object',
    properties: {
      ok: { type: 'boolean' },
      data: { type: 'object' },
      error: {
        type: 'object',
        properties: { code: { type: 'string' }, message: { type: 'string' } },
        required: ['code', 'message'],
      },
    },
    required: ['ok'],
  } as const,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export async function executeTemplateBuilderSearchReference({
  canEdit,
  input,
  signal,
}: TemplateToolContext): Promise<TemplateWebMCPToolResult<{ entries: unknown[] }>> {
  const invocationId = createInvocationId();
  const startedAt = Date.now();
  logTemplateWebMCP('invocation_start', {
    tool: TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL.name,
    invocationId,
  });

  const finish = (
    result: TemplateWebMCPToolResult<{ entries: unknown[] }>,
    outcome: 'success' | 'validation_failure' | 'cancellation' | 'unexpected_failure',
  ) => {
    logTemplateWebMCP('invocation_complete', {
      tool: TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL.name,
      invocationId,
      outcome,
      durationMs: Date.now() - startedAt,
    });
    return result;
  };

  try {
    if (signal?.aborted) {
      return finish(
        mcpToolResult(toolError('UNEXPECTED', 'The tool invocation was cancelled.')),
        'cancellation',
      );
    }
    if (!canEdit) {
      return finish(
        mcpToolResult(toolError('UNAUTHORIZED', 'Template Builder edit permission is required.')),
        'validation_failure',
      );
    }
    if (input != null && !isRecord(input)) {
      return finish(
        mcpToolResult(
          toolError('INVALID_INPUT', 'template_builder_search_reference expects an object.'),
        ),
        'validation_failure',
      );
    }
    const record = isRecord(input) ? input : {};
    const found = searchReference({
      query: typeof record.query === 'string' ? record.query : undefined,
      entryId: typeof record.entryId === 'string' ? record.entryId : undefined,
      kind: typeof record.kind === 'string' ? record.kind : undefined,
      limit: typeof record.limit === 'number' ? record.limit : undefined,
      includePayload:
        typeof record.includePayload === 'boolean' ? record.includePayload : undefined,
    });
    if ('error' in found) {
      return finish(mcpToolResult(toolError('INVALID_INPUT', found.error)), 'validation_failure');
    }
    return finish(mcpToolResult(toolSuccess(found)), 'success');
  } catch (error) {
    return finish(
      mcpToolResult(
        toolError('UNEXPECTED', 'An unexpected error occurred.', {
          message: error instanceof Error ? error.message : String(error),
        }),
      ),
      'unexpected_failure',
    );
  }
}
